import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  HostListener,
  forwardRef,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy
} from '@angular/core';

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  FormControl,
  Validator
} from '@angular/forms';

import * as momentNs from 'moment';
const moment = momentNs;

import { DatepickerOverlayService } from './datepicker-overlay.service';
import { DatepickerInputDirective } from './datepicker-input/datepicker-input.directive';
import { DatepickerValidatorService } from './datepicker-validator.service';
import { UtilService } from './shared/util.service';
import { OverlayService } from './shared/overlay.service';

/**
 * Provider Expression that allows custom input to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 */
export const DATEPICKER_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatepickerComponent),
  multi: true
};

/**
 * Provider Expression that allows custom validators.
 */
export const DATEPICKER_CONTROL_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => DatepickerComponent),
  multi: true
};

/**
 * Datepicker component is responsible for hiding & showing the datepicker.
 * Implements ControlValueAccessor so datepicker will work as a custom
 * form control.
 * Implements Validator to support custom validations.
 */
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'cfx-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    DATEPICKER_CONTROL_VALUE_ACCESSOR,
    DATEPICKER_CONTROL_VALIDATOR,
    DatepickerOverlayService,
    OverlayService,
    DatepickerValidatorService
  ]
})
export class DatepickerComponent implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy {
  datepicketMobile: Boolean;
  mobileInput= 'hide';
  // the value of the input
  value = '';

  // on mobile we use different format
  mobileDateValue: string;

  // mask for the input
  mask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];

  // if the calendar is currently shown
  calendarShown: boolean;

  // device type. since on mobile we show a native datepicker
  // input type
  deviceType: string;

  // random ID for accessability
  randomID = 'datepicker_' + Math.floor(Math.random() * 10000000);

  // if calendar is disabled
  @Input() disabled: boolean;

  // datepicker label
  @Input() label: string;

  // datepicker id (should be required for proper accessability)
  @Input() id: string;

  // if the component is valid (for form validation CSS classes)
  @Input() invalid: boolean;

  // max date for datepicker (todays date is default)
  // TODO: set default in this component (curently its set in the calendar service)
  @Input() maxDate = moment().format('MM/DD/YYYY'); // todays date

  // min date for datepicker (01/01/1900 is default)
  // TODO: set default in this component (curently its set in the calendar service)
  @Input() minDate = moment('01/01/1900', 'MM/DD/YYYY').format('MM/DD/YYYY');

  /**
   * Datepicker input as an HTML element. Needed to in click listener
   * and to send to overlay service in order for the datepicker calendar
   * to tether to the input.
   */
  @ViewChild(DatepickerInputDirective, { read: ElementRef }) inputElement;

  /**
   * Datepicker input directive. Needed to send to overlay service in order
   * for the datepicker calendar to access the value and set the value
   * of the input.
   */
  @ViewChild(DatepickerInputDirective) inputComponent;

  /**
   * The datepicker button. Need to make sure we don't close calendar
   * in documen click listener if user clicks the button.
   */
  @ViewChild('btnDatepicker', { read: ElementRef }) toggleButtonElement;

  /**
   * The calendar should close if we click outside of it
   */
  @HostListener('document:click', ['$event']) onDocumentClick(event) {
    if (
      // overlay exists
      this.datepickerOverlaySvc.opened()

      // and didn't click the input box
      // && !this.inputElement.nativeElement.contains(event.target)

      // and didn't click the toggle button
      && !this.toggleButtonElement.nativeElement.contains(event.target)

      // and didn't click inside the calendar
      && !this.datepickerOverlaySvc.overlayElement.contains(event.target)
    ) {
     this.closeDatepickerCalendar();
    }
  }

  constructor(
    private datepickerOverlaySvc: DatepickerOverlayService,
    private datepickerValidatorSvc: DatepickerValidatorService,
    private utilSvc: UtilService
  ) { }

  /**
   * Called when the input value is updated via the calendar
   * @param value the new input value
   * @param dayChange if the day has changed we need to hide calendar
   */
  calendarValueChange({ value, dayChange }) {
    this.onInput(value);
    if (dayChange) {
      this.closeDatepickerCalendar();
    }
  }

  /**
   * Toggle datepicker calendar visiblity
   */
  toggleDatepickerCalendar() {
    if (this.disabled) { return; }

    this.calendarShown = !this.calendarShown;

    if (this.calendarShown) {
      this.datepickerOverlaySvc.open(this.inputElement, this.inputComponent);
    } else {
      this.datepickerOverlaySvc.close();
    }
  }

  private closeDatepickerCalendar() {
    this.datepickerOverlaySvc.close();
    this.calendarShown = false;
  }

  ngOnInit() {
    this.datepickerValidatorSvc._maxDate = this.maxDate;
    this.datepickerValidatorSvc._minDate = this.minDate;

    this.deviceType = this.utilSvc.getDeviceType();
  }

  ngOnDestroy() {
    this.closeDatepickerCalendar();
  }

  /**
   * Keeps the max & min date updated in the service
   * TODO: this is hacky. Look into passing the values
   * using the overlay service.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxDate) {
      this.datepickerValidatorSvc._maxDate = changes.maxDate.currentValue;
    }
    if (changes.minDate) {
      this.datepickerValidatorSvc._minDate = changes.minDate.currentValue;
    }
  }


  /**
   * ControlValueAccessor methods for custom form component
   * implementation
   */

  /**
   * Called when the input is blurred. Needed to properly implement ControlValueAccessor.
   */
  onTouched: () => any = () => {};

  /**
   * Called when input value is changed
   */
  private controlValueAccessorChangeFn: (value: any) => void = () => {};

  onInput(value) {
    // mobile support. For mobile input we send a dom event instead of date
    // value so just checking for target here to see if it is mobile input
    // sending the event.
    if (value.target && value.target.value !== '') {
      value = value.target.value;
      value = moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY');
      this.value = value;
      this.mobileDateValue = moment(value, 'MM/DD/YYYY').format('YYYY-MM-DD');
    } else if (value.target && value.target.value === '') {
      value = '';
      this.value = '';
      this.mobileDateValue = '';
    } else {
      this.value = value;
      this.mobileDateValue = value;
    }
    this.controlValueAccessorChangeFn(value);
  }

  // Implemented as part of ControlValueAccessor.
  writeValue(value) {
    // mobile date value is needs to be in YYYY-MM-DD format per
    // W3C
    this.mobileDateValue = moment(value, 'MM/DD/YYYY').format('YYYY-MM-DD');
    this.value = value;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: any) => void) {
    this.controlValueAccessorChangeFn = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  /**
   * Input Validation
   */

  /**
   * Needed to implement 'Validator' interface for
   * custom validation
   */
  validate(c: FormControl) {
    return this.datepickerValidatorSvc.getErrors(c.value);
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/datepicker/datepicker.component.ts